<template>
  <v-card flat color="transparent">
    <v-card outlined color="grey lighten-2" width="310">
      <v-form ref="form" v-model="interalValid">
        <div class="container my-n1 ml-n1">
          <template v-if="!specialFormat">
            <div style="max-height: 40px" v-if="numberplateImage">
              <img :src="numberplateImage" style="max-height: 40px" />
            </div>
            <div style="width: 80px">
              <v-text-field
                ref="cityField"
                v-model="city"
                background-color="white"
                class="mb-n6 mx-1 centered-input"
                single-line
                dense
                maxlength="3"
                autocomplete="off"
                type="text"
                outlined
                @keyup="keyUpCityField"
                @keydown="onkeydownCity"
                data-test-form-numberplate-city
                :disabled="disabled"
                light
              />
            </div>
            <div style="max-height: 40px" v-if="numberplateBadge">
              <img :src="numberplateBadge" style="max-height: 40px" />
            </div>

            <div style="width: 80px">
              <v-text-field
                ref="letterField"
                background-color="white"
                v-model="letters"
                maxlength="2"
                class="mb-n6 mx-1 centered-input"
                autocomplete="off"
                single-line
                dense
                outlined
                @keyup="keyUpLetterField"
                @keydown="onkeydown"
                data-test-form-numberplate-name
                :disabled="disabled"
                light
              />
            </div>
            <div style="width: 113px">
              <v-text-field
                ref="numberField"
                v-model="numbers"
                background-color="white"
                class="mb-n6 ml-1 mr-n2 centered-input"
                style="text-align: center"
                maxlength="6"
                autocomplete="off"
                single-line
                dense
                outlined
                @keyup="keyUpNumberField"
                data-test-form-numberplate-number
                :disabled="disabled"
                light
              /></div
          ></template>
          <template v-else>
            <div style="max-height: 40px" v-if="numberplateImage">
              <img :src="numberplateImage" style="max-height: 40px" />
            </div>
            <div style="width: 313px">
              <v-text-field
                v-model="numberPlate"
                background-color="white"
                maxlength="20"
                class="mb-n6 mx-2"
                autocomplete="off"
                single-line
                dense
                outlined
                @keyup="keyUpSpecialNumberplate()"
                data-test-form-numberplate-special
                :disabled="disabled"
                light
              />
            </div>
            <div style="max-height: 40px" class="mr-n2" v-if="numberplateBadge">
              <img :src="numberplateBadge" style="max-height: 40px" />
            </div>
          </template>
        </div>
      </v-form>
    </v-card>
    <v-card flat color="transparent" v-if="isGermanNumberplate && !disabled">
      <v-card-text class="ml-n4 mt-n2" data-test-form-numberplate-change>
        <span style="cursor: pointer" @click="toggle">
          <span v-if="!specialFormat"
            ><v-icon small color="info">mdi-autorenew</v-icon>{{ $t("components.numberplateForm.hint") }}</span
          >
          <span v-else
            ><v-icon small color="info">mdi-autorenew</v-icon>{{ $t("components.numberplateForm.hintAlt") }}</span
          >
        </span>
      </v-card-text>
    </v-card>
  </v-card>
</template>

<script lang="ts">
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { NumberplateModule } from "@/store/modules/numberplateStore";
import { Component, Prop, Ref, Watch } from "vue-property-decorator";

interface INumberplateImage {
  numberPlateImage?: AssetEnum;
  badeImage?: AssetEnum;
}

@Component({
  components: {}
})
export default class NumberplateForm extends DarkModeHighlightMixin {
  @Prop()
  value!: string;

  @Prop({ default: false })
  valid!: string;

  @Prop({ default: false })
  disabled!: boolean;

  @Prop({ default: CountryCodeEnum.germany })
  countryCode!: CountryCodeEnum;

  @Ref("letterField")
  private readonly letterField!: HTMLElement;

  @Ref("numberField")
  private readonly numberField!: HTMLElement;

  @Ref("form")
  private readonly form!: Vue & { validate: () => boolean };

  get isGermanNumberplate(): boolean {
    return this.countryCode == CountryCodeEnum.germany;
  }

  @Watch("value")
  onValueChange(value: string) {
    this.$log.debug(value);
    this.privateNumberplate = value;
    this.tryParseNumberplate();
  }

  @Watch("privateNumberplate", { deep: true })
  emitValid() {
    if (this.specialFormat) {
      this.$emit("valid", !!this.privateNumberplate.length);

      return;
    }

    if (this.privateCity.length && this.privateLetters.length && this.privateNumbers.length) {
      this.$emit("valid", true);
    } else {
      this.$emit("valid", false);
    }
  }

  @Watch("privateNumberplate", { deep: true })
  emitUpdated() {
    this.$emit("input", this.privateNumberplate);
  }

  privateNumberplate = this.value;
  interalValid = this.valid;

  onkeydownCity(event: KeyboardEvent) {
    if (!/[a-zA-ZÄÖÜäöü]/i.test(event.key)) {
      event.preventDefault();
      return false;
    }
  }

  onkeydown(event: KeyboardEvent) {
    if (!/[a-zA-Z]/i.test(event.key)) {
      event.preventDefault();
      return false;
    }
  }

  /**
   * is the numberplate a speical format?
   */
  get specialFormat(): boolean {
    return NumberplateModule.isSepcialFormat;
  }

  /**
   * set special format flag to true if:
   * numberplate from query is not parsable
   * or user clicks on special format
   */
  set specialFormat(value: boolean) {
    NumberplateModule.setSepcialFormat(value);
  }

  disableFocusLetters = false;
  disableFocusNumbers = false;

  privateCity = "";
  privateLetters = "";
  privateNumbers = "";

  toggle() {
    this.numberPlate = "";
    this.privateNumberplate = "";
    this.privateCity = "";
    this.privateLetters = "";
    this.privateNumbers = "";
    this.specialFormat = !this.specialFormat;
  }

  get countryConfigMap() {
    return new Map<CountryCodeEnum, INumberplateImage>([
      [
        CountryCodeEnum.germany,
        {
          numberPlateImage: AssetEnum.numberplateEuropeDe,
          badeImage: AssetEnum.numberplateBadge
        }
      ],
      [
        CountryCodeEnum.switzerland,
        {
          numberPlateImage: AssetEnum.numberplateChe,
          badeImage: AssetEnum.numberplateBageChe
        }
      ],
      [
        CountryCodeEnum.austria,
        {
          numberPlateImage: AssetEnum.numberplateEuropeAt
        }
      ]
    ]);
  }

  get imageConfig(): INumberplateImage {
    const imageConfig = this.countryConfigMap.get(this.countryCode);
    if (!imageConfig) {
      return {
        numberPlateImage: AssetEnum.numberplateEuropeGeneric,
        badeImage: AssetEnum.numberplateBadge
      };
    }

    return imageConfig;
  }

  get numberplateImage() {
    if (!this.imageConfig.numberPlateImage) {
      return undefined;
    }
    return AssetRepository.getAsset(this.$vuetify.theme.dark, this.imageConfig.numberPlateImage);
  }

  get numberplateBadge() {
    if (!this.imageConfig.badeImage) {
      return undefined;
    }
    return AssetRepository.getAsset(this.$vuetify.theme.dark, this.imageConfig.badeImage);
  }

  get city(): string {
    return this.privateCity;
  }

  set city(city: string) {
    this.privateCity = city;
  }

  get letters(): string {
    return this.privateLetters;
  }
  set letters(letters: string) {
    this.privateLetters = letters;
  }

  get numbers(): string {
    return this.privateNumbers;
  }
  set numbers(numbers: string) {
    this.privateNumbers = numbers;
  }

  get numberPlate(): string {
    return this.privateNumberplate;
  }

  set numberPlate(numberplate: string) {
    this.privateNumberplate = numberplate;
    this.emitUpdated();
  }

  setNumberPlate() {
    this.privateNumberplate =
      this.privateCity.toUpperCase().trim() +
      "-" +
      this.privateLetters.toUpperCase().trim() +
      " " +
      this.privateNumbers.toUpperCase().trim();
  }

  keyUpCityField() {
    this.privateCity = this.city.toUpperCase();
    this.privateCity = this.privateCity.trim();
    this.setNumberPlate();
    if (this.city.length <= 2 || this.disableFocusLetters) {
      return;
    }
    this.disableFocusLetters = true;
    setTimeout(() => {
      this.letterField.focus();
      this.form.validate();
    }, 50);
  }

  keyUpLetterField() {
    this.privateLetters = this.privateLetters.toUpperCase();
    this.privateLetters = this.privateLetters.trim();
    this.setNumberPlate();
    if (this.privateLetters.length <= 1 || this.disableFocusNumbers) {
      return;
    }
    this.disableFocusNumbers = true;
    setTimeout(() => {
      this.numberField.focus();
      this.form.validate();
    }, 50);
  }

  keyUpNumberField() {
    /**
     * Transform to Uppercase
     */
    this.privateNumbers = this.privateNumbers.toUpperCase();

    /**
     * Divide String into Alpha and digits
     */
    let digits = this.privateNumbers.replace(/[^0-9]/g, "");
    const spaces = this.privateNumbers.replace(/[^ ]/g, "");
    let alpha = this.privateNumbers.replace(/[^EehH]/g, "");

    /**
     * Only allow 4 digits
     */
    if (digits.length > 4) {
      digits = digits.slice(0, 4);
    }

    /**
     * Check if string contains characters
     */
    if (alpha.length) {
      /**
       * If more than one character only use first one since numberplate can contains only E or H
       */
      if (alpha.length > 1) {
        alpha = alpha.slice(0, 1);
      }

      this.privateNumbers = digits + " " + alpha;
    } else if (digits.length && spaces.length) {
      this.privateNumbers = digits + " ";
    } else {
      this.privateNumbers = digits;
    }

    this.setNumberPlate();
  }

  keyUpSpecialNumberplate() {
    this.numberPlate = this.numberPlate.toUpperCase();
    this.emitUpdated();
  }
  tryParseNumberplate() {
    this.$log.debug("tryParseNumberplate", this.numberPlate);
    let numberplateStringParts = this.numberPlate.split(/-| |_/);

    numberplateStringParts = numberplateStringParts.filter((str) => str !== "");

    if (this.specialFormat === true) {
      this.keyUpSpecialNumberplate();

      return;
    }

    if (numberplateStringParts.length === 0) {
      return;
    } else if (
      numberplateStringParts.length >= 3 &&
      numberplateStringParts[0].length <= 3 &&
      numberplateStringParts[1].length <= 2 &&
      numberplateStringParts[2].length <= 6 &&
      numberplateStringParts[0].match(new RegExp(/[a-zA-ZÄÖÜäöü]/)) &&
      numberplateStringParts[1].match(new RegExp(/[a-zA-Z]/))
    ) {
      this.specialFormat = false;
      this.privateCity = numberplateStringParts[0].toUpperCase();
      this.privateLetters = numberplateStringParts[1].toUpperCase();

      this.privateNumbers = numberplateStringParts[2].toUpperCase();
      if (numberplateStringParts.length > 3) {
        for (let i = 3; i < numberplateStringParts.length; i++) {
          this.privateNumbers = this.privateNumbers + " " + numberplateStringParts[i].toUpperCase();
        }
      }

      this.setNumberPlate();
    } else if (
      numberplateStringParts.length === 2 &&
      numberplateStringParts[0].length <= 3 &&
      numberplateStringParts[1].length <= 2 &&
      numberplateStringParts[0].match(new RegExp(/[a-zA-ZÄÖÜäöü]/)) &&
      numberplateStringParts[1].match(new RegExp(/[a-zA-Z]/))
    ) {
      this.specialFormat = false;
      this.privateCity = numberplateStringParts[0].toUpperCase();
      this.privateLetters = numberplateStringParts[1].toUpperCase();
    } else if (
      numberplateStringParts.length === 1 &&
      numberplateStringParts[0].length <= 3 &&
      numberplateStringParts[0].match(new RegExp(/[a-zA-ZÄÖÜäöü]/))
    ) {
      this.specialFormat = false;
      this.privateCity = numberplateStringParts[0].toUpperCase();
    } else {
      this.keyUpSpecialNumberplate();
      this.specialFormat = true;
    }
  }

  mounted() {
    try {
      this.privateNumberplate = this.value;

      if (!this.isGermanNumberplate) {
        this.keyUpSpecialNumberplate();
        this.specialFormat = true;
        this.emitValid();
        return;
      }

      this.tryParseNumberplate();
      this.emitValid();
    } catch (error) {
      this.$log.error(error);
    }
  }
}
</script>

<style scoped>
.container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.text {
  font-size: 20px;
  padding-left: 20px;
}

.centered-input >>> input {
  text-align: center;
}
</style>
